import { t } from '../i18n/i18n';
/**
 * The "click to look around" prompt for mouse-look
 * (camera-and-controls.md amendment, 2026-08-25, fifth).
 *
 * Turning the camera needs the pointer captured, and Pointer Lock can only be
 * requested from a real user gesture — so there is always a moment, at the
 * start of a run and after every Escape, where moving the mouse does nothing.
 * Without something on screen saying so, that reads as the mouse being broken.
 * This is the whole reason this file exists; it is one line of text doing the
 * work of a bug report that never gets filed.
 *
 * Never shown on a coarse pointer. A phone has no pointer to lock and no
 * Escape to press, so the prompt there would be a permanent instruction to do
 * something impossible — in-game touch controls are still the open gap they
 * were before this amendment (`input-controller.ts`), not something a hint can
 * paper over.
 */
export class MouseLookPrompt {
  private readonly root: HTMLDivElement;
  private readonly supported: boolean;
  private suppressed = false;
  private locked = false;

  constructor(container: HTMLElement) {
    this.supported = typeof window !== 'undefined'
      && !!window.matchMedia?.('(pointer: fine)').matches
      && typeof Element !== 'undefined' && 'requestPointerLock' in Element.prototype;

    this.root = document.createElement('div');
    this.root.className = [
      'fixed', 'left-1/2', '-translate-x-1/2', 'bottom-[18%]', 'z-[1150]',
      'pointer-events-none', 'select-none', 'hidden',
      'px-4', 'py-2', 'frame-pewter',
      'text-center', 'text-text', 'text-[clamp(0.8rem,1.8vw,0.95rem)]',
    ].join(' ');

    const line = document.createElement('div');
    line.textContent = t('Click to look around');
    const sub = document.createElement('div');
    sub.className = 'text-text-dim text-[0.75em] mt-0.5';
    sub.textContent = t('{key} releases the mouse', { key: 'Esc' });
    this.root.append(line, sub);

    container.appendChild(this.root);
  }

  /** Called from the camera's `onLockChange`: shown whenever the pointer is not captured. */
  setLocked(locked: boolean): void {
    this.locked = locked;
    this.render();
  }

  /**
   * Hides the prompt regardless of lock state — for the run-start fly-through,
   * which drives the camera itself and where "click to look" would be both
   * wrong (the click skips the preview) and competing with the Skip button.
   */
  setSuppressed(suppressed: boolean): void {
    this.suppressed = suppressed;
    this.render();
  }

  /** Visible only when there is genuinely something for the player to do about it. */
  private render(): void {
    const visible = !this.locked && this.supported && !this.suppressed;
    this.root.classList.toggle('hidden', !visible);
  }

  dispose(): void {
    this.root.remove();
  }
}
